import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * SkuSettingsContext — global, cached read of /api/sku/settings so any
 * screen in the app can decide whether to show the "OE Part Number" column
 * post-migration without each component fetching the same data again.
 *
 * Updates every 5 minutes; consumers also get `refresh()` for manual refetch.
 */
const SkuSettingsContext = createContext({
  migrationStatus: 'not_started',
  loading: true,
  refresh: () => {},
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SkuSettingsProvider({ children }) {
  const [migrationStatus, setMigrationStatus] = useState('not_started');
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sku/public-status`);
      setMigrationStatus(data?.migration_status || 'not_started');
    } catch (_e) {
      // Public endpoint failures (logged-out users) — silent fallback.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    const t = setInterval(fetchSettings, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchSettings]);

  return (
    <SkuSettingsContext.Provider value={{ migrationStatus, loading, refresh: fetchSettings }}>
      {children}
    </SkuSettingsContext.Provider>
  );
}

export function useSkuSettings() {
  return useContext(SkuSettingsContext);
}

/**
 * Returns true when the OE Part Number column should be VISIBLE for a given
 * item/order. Per product requirement:
 *   - SKU migration must be `deployed`.
 *   - The item's parent order must NOT be frozen (display_old_jp_numbers).
 *     Frozen orders keep showing old JP numbers (which already encode the OE),
 *     so adding the OE column is redundant.
 */
export function useShowOeColumn(order) {
  const { migrationStatus } = useSkuSettings();
  const isFrozen = !!(order && order.display_old_jp_numbers === true);
  return migrationStatus === 'deployed' && !isFrozen;
}
